'use client'
import type { TransitionProps } from '@mui/material/transitions'
import type React from 'react'

import { motion, useReducedMotion } from 'motion/react'
import { forwardRef, useRef } from 'react'

import { HERO_MOTION } from './constants'

type DialogMotionTransitionProps = TransitionProps & {
  children: React.ReactElement
  // MUI passes its internal state to slot components; it must not reach the DOM.
  ownerState?: unknown
}

interface DialogMotionConfig {
  y: number
  scale: number
  duration: number
  ease: typeof HERO_MOTION.dialog.ease | typeof HERO_MOTION.metricDialog.ease
}

/**
 * Creates a MUI Dialog `slots.transition` driven by motion: fade, scale and a small rise.
 * Calls the MUI lifecycle callbacks so the Modal knows when to show and hide.
 */
const createDialogMotionTransition = ({ y, scale, duration, ease }: DialogMotionConfig) => {
  const DialogMotionTransition = forwardRef<HTMLDivElement, DialogMotionTransitionProps>(({
    in: isIn = false,
    children,
    onEnter,
    onEntered,
    onExit,
    onExited,
    appear: _appear,
    timeout: _timeout,
    addEndListener: _addEndListener,
    onEntering: _onEntering,
    onExiting: _onExiting,
    mountOnEnter: _mountOnEnter,
    unmountOnExit: _unmountOnExit,
    style,
    ownerState: _ownerState,
    ...other
  }, ref) => {
    const reduceMotion = useReducedMotion()
    const nodeRef = useRef<HTMLDivElement | null>(null)

    const setRefs = (node: HTMLDivElement | null) => {
      nodeRef.current = node
      if (typeof ref === 'function') {
        ref(node)
      }
      else if (ref) {
        ref.current = node
      }
    }

    return (
      <motion.div
        ref={setRefs}
        {...(other as React.ComponentProps<typeof motion.div>)}
        style={{ height: '100%', outline: 0, ...style }}
        initial={false}
        animate={isIn
          ? { opacity: 1, scale: 1, y: 0 }
          : { opacity: 0, scale: reduceMotion ? 1 : scale, y: reduceMotion ? 0 : y }}
        transition={{ duration: reduceMotion ? 0 : duration, ease }}
        onAnimationStart={() => {
          if (!nodeRef.current) return
          if (isIn) {
            onEnter?.(nodeRef.current, false)
          }
          else {
            onExit?.(nodeRef.current)
          }
        }}
        onAnimationComplete={() => {
          if (!nodeRef.current) return
          if (isIn) {
            onEntered?.(nodeRef.current, false)
          }
          else {
            onExited?.(nodeRef.current)
          }
        }}
      >
        {children}
      </motion.div>
    )
  })

  DialogMotionTransition.displayName = 'DialogMotionTransition'
  return DialogMotionTransition
}

const GoalDialogTransition = createDialogMotionTransition(HERO_MOTION.dialog)
const MetricDialogTransition = createDialogMotionTransition(HERO_MOTION.metricDialog)

export { createDialogMotionTransition, GoalDialogTransition, MetricDialogTransition }
